import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';

/**
 * Detects Google Maps authentication / API-restriction failures and surfaces a
 * clear in-app banner to the user. This is *much* more useful than Google's
 * small "This page can't load Google Maps correctly" overlay, which is easy to
 * miss and often clipped by our dark UI.
 *
 * Call `<MapErrorBanner />` once on any page that renders a Google Map. It will
 * listen for the global `gm_authFailure` callback and for `ApiTargetBlockedMapError`
 * in the JS console.
 */
const MapErrorBanner = () => {
  const [error, setError] = useState(null);

  useEffect(() => {
    // Primary: Google calls `window.gm_authFailure` for API key / referrer problems.
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => {
      setError({
        code: 'AUTH_FAILURE',
        message: 'Google Maps rejected this request. The API key is either invalid, restricted to a different domain, or has required APIs disabled.'
      });
      if (typeof prev === 'function') prev();
    };

    // Secondary: scrape console warnings for common restriction errors.
    const origErr = console.error;
    console.error = function (...args) {
      try {
        const text = args.map((a) => (typeof a === 'string' ? a : '')).join(' ');
        if (text.includes('ApiTargetBlockedMapError')) {
          setError({
            code: 'API_TARGET_BLOCKED',
            message: 'Maps JavaScript API is blocked by this Google API key\'s restrictions. Add "Maps JavaScript API" to the allowed APIs on the key.'
          });
        } else if (text.includes('RefererNotAllowedMapError')) {
          setError({
            code: 'REFERRER_BLOCKED',
            message: 'This domain is not whitelisted on the Google Maps API key\'s HTTP referrer restriction list.'
          });
        } else if (text.includes('ApiNotActivatedMapError')) {
          setError({
            code: 'API_NOT_ACTIVATED',
            message: 'Maps JavaScript API is not enabled in your Google Cloud project.'
          });
        } else if (text.includes('BillingNotEnabled')) {
          setError({
            code: 'BILLING',
            message: 'Billing is not enabled on the Google Cloud project that owns this Maps API key.'
          });
        }
      } catch (e) { /* ignore */ }
      return origErr.apply(console, args);
    };

    return () => {
      console.error = origErr;
      if (window.gm_authFailure === undefined || window.gm_authFailure.toString().includes('setError')) {
        window.gm_authFailure = prev;
      }
    };
  }, []);

  if (!error) return null;

  return (
    <div
      className="bg-amber-500/10 border border-amber-500/40 text-amber-100 rounded-sm px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="map-error-banner"
    >
      <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-amber-200 text-sm">Maps not loading: {error.code.replaceAll('_', ' ')}</p>
        <p className="text-amber-100/90 text-xs mt-0.5">{error.message}</p>
        <a
          href="https://console.cloud.google.com/apis/credentials"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-100 underline mt-1"
        >
          Open Google Cloud Console <ExternalLink className="w-3 h-3" />
        </a>
        <p className="text-amber-100/70 text-[11px] mt-1 font-mono">
          APIs &amp; Services → Credentials → [your Maps key] → API restrictions → add "Maps JavaScript API"
        </p>
      </div>
    </div>
  );
};

export default MapErrorBanner;
